import { Theme, useTheme } from "@mui/material";
import Plot from "components/plotly/LocalisedPlot";
import { LegendHint } from "../LegendHint";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { TFunction } from "i18next";
import { Layout, PlotData } from "plotly.js";
import { useMemo } from "react";
import { UnitType } from "src/store/api/generatedApi";
import { defaultLayout, generalTickFormatting } from "../plotting";
import { GroupedDeliverySeries } from "../types";

interface DeliveriesPlotProps {
  deliverySeries: GroupedDeliverySeries;
  selectedMaterial: string | null;
  selectedSeriesTypes: string[];
}

export const DeliveriesPlot = ({
  deliverySeries,
  selectedMaterial,
  selectedSeriesTypes,
}: DeliveriesPlotProps) => {
  const { t } = useTenantTranslation();
  const units = useUnitsFormatter(true);
  const theme = useTheme();

  const figure = useMemo(() => {
    const newData = buildInitialFigData(deliverySeries, t, theme);
    return {
      data: updateFigData(newData, selectedMaterial, selectedSeriesTypes),
      layout: buildLayout(defaultLayout, t, units),
    };
  }, [deliverySeries, selectedMaterial, selectedSeriesTypes, t, theme, units]);

  return (
    <div style={{ width: "100%" }}>
      <Plot
        data={figure.data}
        layout={figure.layout}
        style={{ width: "100%" }}
        useResizeHandler
      />
      <LegendHint />
    </div>
  );
};

const buildInitialFigData = (
  deliverySeries: GroupedDeliverySeries,
  t: TFunction,
  theme: Theme
): Partial<PlotData>[] => {
  const figData: Partial<PlotData>[] = [];

  // Build all traces for all materials
  for (const [key, series] of deliverySeries.values) {
    // Add MTD deliveries trace
    figData.push({
      x: series.date,
      y: series.deliveryMassMtd,
      type: "scatter",
      mode: "lines+markers",
      visible: false,
      name: `${key.materialName} - ${t("monthToDateDeliveries")}`,
      line: {
        color: theme.palette.data.blue,
        width: 3,
      },
      marker: {
        size: 8,
      },
      customdata: [key.materialName, "mtd"],
    });

    figData.push({
      x: series.date,
      y: series.expectedMassMtd,
      type: "scatter",
      mode: "lines",
      visible: false,
      name: `${key.materialName} - ${t("expectedMonthToDateDeliveries")}`,
      line: {
        color: theme.palette.data.orange,
        width: 3,
        dash: "dot",
      },
      customdata: [key.materialName, "expectedMtd"],
    });

    // Add monthly purchases trace
    figData.push({
      x: series.date,
      y: series.monthPurchasedMass,
      type: "scatter",
      mode: "lines",
      visible: false,
      name: `${key.materialName} - ${t("monthPurchasedTotal")}`,
      line: {
        color: theme.palette.data.lightblue,
        width: 4,
        shape: "hv",
        dash: "dash",
      },
      customdata: [key.materialName, "purchases"],
    });
  }

  return figData;
};

const updateFigData = (
  figData: Partial<PlotData>[],
  selectedMaterial: string | null,
  selectedSeriesTypes: string[]
): Partial<PlotData>[] => {
  return figData.map((trace) => ({
    ...trace,
    visible:
      trace.customdata?.[0] === selectedMaterial &&
      selectedSeriesTypes.includes(trace.customdata[1] as string),
  }));
};

const buildLayout = (
  baseLayout: Partial<Layout>,
  t: TFunction,
  units: (key: UnitType) => string
): Partial<Layout> => {
  return {
    ...baseLayout,
    hovermode: "x unified",
    xaxis: {
      title: { text: t("date") },
    },
    yaxis: {
      title: { text: `${t("deliveries")} (${units("mass")})` },
      tickformat: generalTickFormatting,
    },
  };
};
